import { useState, type FormEvent } from "react";
import { Redirect, useLocation } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/Button";
import "./login.css";

interface RedirectState {
  from?: string;
}

/**
 * Auth stub. Calls the mock `login()` and drops the returned session into
 * memory only — see AuthContext for why nothing here is a token to store.
 * Real auth (charter C-3 local admin bootstrap, C-5 OIDC/SSO) replaces the
 * form submit with a server-verified session cookie and, for SSO, a
 * redirect to the identity provider instead of this form at all.
 */
export function LoginPage() {
  const { session, login, isAuthenticating, authError } = useAuth();
  const [, navigate] = useLocation();
  // ProtectedRoute stashes where the operator was headed on the History API
  // entry it redirected from; read it back so sign-in lands them there.
  const redirectState = useHistoryState<RedirectState | null>();
  // Default to "/" rather than a fleet page: the role is not known until the
  // session comes back, and the landing route resolves it (App.tsx's
  // RoleLanding) — a billing_admin must not be dropped on a fleet view it
  // cannot load.
  const destination = redirectState?.from ?? "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  if (session) {
    return <Redirect to={destination} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await login(username, password);
      navigate(destination, { replace: true });
    } catch {
      // authError from context already carries the operator-facing message.
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__brand-mark" aria-hidden="true" />
          <span className="login__brand-name">Comebye</span>
        </div>
        <p className="login__subtitle">Sign in to your control plane</p>

        <form className="login__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          {authError && (
            <p className="login__error" role="alert">
              {authError}
            </p>
          )}

          <label className="login__field">
            <span>Username</span>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>

          <label className="login__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          <Button type="submit" variant="primary" disabled={isAuthenticating} className="login__submit">
            {isAuthenticating ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="login__divider">
          <span>or</span>
        </div>

        <Button variant="secondary" disabled title="Single sign-on is coming soon" className="login__sso">
          Continue with SSO
        </Button>
        <p className="login__note">Single sign-on (OIDC) is coming soon.</p>
      </div>
    </div>
  );
}
